import {
	Box,
	Grid,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import { MutableRefObject, useCallback, useEffect, useState } from 'react'

import useAPIFetch from '@hooks/useAPIFetch'
import { KeyValPair } from '@models/common.model'
import styled from '@emotion/styled'
import CustomizedSearch from '@components/customized/search'
import useQuery, { paramsToObject } from '@hooks/useQuery'
import { unflatten, flatten } from 'flat'
import { cloneDeep } from 'lodash'
import { FiltersPopover } from './filtersPopover'

interface Sorting {
	by: string
	order_by: 'ASC' | 'DESC'
}

export interface Criteria {
	key: string
	name: string
	type?: 'StringFilter' | 'SelectionFilter' | 'DateFilter' | 'ActiveFilter'
	options?: KeyValPair[]
}

export interface FilterCriteria {
	target: string
	operator: string
	value: any[]
}

export interface Pagination {
	page: number
	page_size: number
}

export type useTableViewProps<T> = {
	columns: {
		key: string
		desc: string
		hidden?: boolean
		renderCell?: (
			record: T,
			navigate?: NavigateFunction,
			columnIndex?: number,
			rowIndex?: number,
		) => JSX.Element
		width?: string | number
		disableSort?: boolean
		disableFilter?: boolean
	}[]
	tableHeight?: number | string
	rowsPerPage?: number
	reloadCallback: (req?: any) => Promise<{ data: T[]; total_count: number } | undefined>
	onRowClick?: (navigate: NavigateFunction, record: T) => void
	toolbar?: boolean
	customRow?: boolean
	mountedRef: MutableRefObject<boolean>
	extraButton?: JSX.Element
	hiddenHeader?: boolean
	criterion?: Criteria[]
	haveGlobalSearch?: boolean
	haveNoFilter?: boolean
	defaultFilterCriteria?: any
	defaultSorter?: any
}

const FunctionBar = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	justifyContent: 'flex-end',
	gap: '8px',
	marginBottom: '8px',
}))

export const TableView = function <T>(props: useTableViewProps<T>) {
	const theme = useTheme()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const searchParams = useQuery()
	const { pathname } = useLocation()
	const { setRequest, isLoading } = useAPIFetch()
	const [isLoadingState, setLoadingState] = useState(!props.mountedRef.current ?? false)

	const [list, setList] = useState<T[] | undefined>()
	const [totalCnt, setTotalCnt] = useState<number>(100)

	const [page, setPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage || 25)
	const [sorting, setSorting] = useState<Sorting | undefined>(props.defaultSorter ?? undefined)
	const [filter, setFilter] = useState<any>(props.defaultFilterCriteria ?? {})
	const [globalSearch, setGlobalSearch] = useState<string[]>([])

	const pagination = { page, page_size: rowsPerPage }

	useEffect(() => {
		if (props.mountedRef.current) {
			onFetch({ page: page, page_size: rowsPerPage }, filter, globalSearch, sorting)
		}
	}, [props.mountedRef.current])

	const onChangeRoute = (data: any | undefined) => {
		const {
			paging = pagination,
			criteria = filter,
			search = globalSearch,
			sorter = sorting,
		} = data || {}
		console.log('onChangeRoute', data)

		const params = flatten(cloneDeep({ ...paging, ...sorter, ...criteria, search })) as any
		console.log('params', params)

		Object.entries(params).forEach(([key, value]) => {
			if (value === undefined || (Array.isArray(value) && value.length === 0)) {
				delete params[key]
			}
		})

		const queries = new URLSearchParams(params)
		navigate(`${pathname}?${queries.toString()}`)
	}

	useEffect(() => {
		const paramsObj = paramsToObject(searchParams)
		if (Object.entries(paramsObj)?.length === 0) {
			onFetch({ page: page, page_size: rowsPerPage }, filter, undefined, sorting)
		} else {
			const paramsNestedObj: any = unflatten(paramsObj)
			if (Object.keys(paramsNestedObj).includes('active')) {
				paramsNestedObj['active'].value = paramsNestedObj['active'].value?.map((value: string) =>
					value === 'true' ? true : false,
				)
			}
			console.log('paramsNestedObj', paramsNestedObj)

			const {
				page: currentPage,
				page_size: pageSize,
				search,
				by,
				order_by,
				...rest
			} = paramsNestedObj

			setPage(currentPage)
			setRowsPerPage(pageSize)
			setFilter(rest ?? {})
			setGlobalSearch(search ?? [])
			let _sort = by && order_by ? { by: by, order_by: order_by } : undefined
			setSorting(_sort)

			onFetch({ page: currentPage, page_size: pageSize }, rest, search, _sort)
		}
	}, [pathname, searchParams])

	const onFetch = debounce(
		(pagination?: Pagination, _filter?: any, search?: any[], sort?: Sorting) => {
			if (!isLoading && props.mountedRef) {
				const paylaod = cloneDeep({
					..._filter,
					pagination,
					search,
					sort,
				})
				console.log('[Datatable onFetch] - payload', paylaod)
				setRequest({
					callback: async (cancelToken: any) => {
						await props.reloadCallback(paylaod).then((res: any) => {
							const { data = [], total_count = 0 } = res || {}
							if (props.mountedRef.current) {
								setList(data || [])
								setTotalCnt(total_count || 0)
								setLoadingState(false)
							}
						})
					},
				})
			}
		},
		800,
	)

	const onSort = (by: string) => {
		const order_by = by !== sorting?.by ? 'ASC' : sorting?.order_by !== 'ASC' ? 'ASC' : 'DESC'
		const _sorting = { by: by, order_by: order_by }
		onChangeRoute({ paging: { page: 1, page_size: rowsPerPage }, sorter: _sorting })
	}

	const onPageChange = (event: unknown, newPage: number) => {
		setPage(newPage + 1)
		let p = { page: newPage + 1, page_size: rowsPerPage }
		onChangeRoute({ paging: p })
	}

	const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value)
		let p = { page: 1, page_size: +event.target.value }
		onChangeRoute({ paging: p })
	}

	const onSubmitFilter = (filter: any) => {
		console.log('onSubmitFilter', filter)
		onChangeRoute({ paging: { page: 1, page_size: rowsPerPage }, criteria: filter })
	}

	const onGlobalSearch = (selectedValue: string | (string | never[])[] | null) => {
		if (selectedValue) {
			onChangeRoute({
				paging: { page: 1, page_size: rowsPerPage },
				search: selectedValue ?? [],
			})
		}
	}

	return (
		<>
			<FunctionBar>
				{props.haveGlobalSearch && (
					<CustomizedSearch
						value={globalSearch}
						setGlobalSearch={setGlobalSearch}
						handleOnSearch={(selectedValue) => {
							onGlobalSearch(selectedValue)
						}}
					/>
				)}
				{props.extraButton}
				{!props.haveNoFilter && <FiltersPopover
					criterion={props.criterion ?? []}
					defaultFilterCriteria={props.defaultFilterCriteria}
					onSubmit={onSubmitFilter}
					filter={filter}
					setFilter={setFilter}
				/>}
			</FunctionBar>

			<TableContainer sx={{ height: props.tableHeight ?? '630px' }}>
				<Table stickyHeader aria-label="sticky table" size="small">
					{!props.hiddenHeader && (
						<TableHead>
							<TableRow key={'header'} sx={{ height: '25px' }}>
								{props.columns
									.filter((header) => !header.hidden)
									.map((column, columnIndex) => {
										return (
											<TableCell
												key={`header_${columnIndex}`}
												sx={{
													backgroundColor: 'white',
													minWidth: column.width ?? '100px',
												}}
												align="left"
												sortDirection={
													sorting && sorting.by === column.key && !column?.disableSort
														? sorting.order_by === 'ASC'
															? 'asc'
															: 'desc'
														: false
												}
											>
												{column.disableSort ? (
													column.desc
												) : (
													<TableSortLabel
														active={sorting && sorting.by === column.key}
														direction={
															sorting && sorting.by?.toLowerCase() === column.key
																? sorting.order_by === 'ASC'
																	? 'asc'
																	: 'desc'
																: undefined
														}
														onClick={() => onSort(column.key)}
														disabled={column.disableSort}
													>
														{column.desc}
													</TableSortLabel>
												)}
												{/* {!column?.disableSort && !column?.disableFilter && <HeaderRowPopover />} */}
											</TableCell>
										)
									})}
							</TableRow>
						</TableHead>
					)}
					{isLoading || isLoadingState ? (
						<TableBody>
							{[...Array(20)].map((e, i) => {
								return (
									<TableRow key={`skeleton_r_${i}`} sx={{ minHeight: '40px' }}>
										{props.columns.map((r, index) => {
											return (
												<TableCell key={`skeleton_${index}`}>
													<Skeleton />
												</TableCell>
											)
										})}
									</TableRow>
								)
							})}
						</TableBody>
					) : (
						<TableBody>
							{list?.map((record, rowIndex) => {
								return (
									<TableRow
										onClick={() => {
											//   dispatch({ type: 'clear' })
											if (props.onRowClick) props.onRowClick(navigate, record)
										}}
										key={`table-record-${rowIndex}`}
										sx={{
											'&:hover': {
												cursor: 'pointer',
												backgroundColor: '#cfcfcf',
											},
											minHeight: '40px',
										}}
									>
										{props.columns
											.filter((header) => !header.hidden)
											.map((column, columnIndex) => (
												<TableCell
													key={`table-record-${rowIndex}-${columnIndex}`}
													sx={{ minWidth: column.width ?? '100px' }}
												>
													{column.renderCell
														? column.renderCell(record, navigate, columnIndex, rowIndex)
														: '-'}
												</TableCell>
											))}
									</TableRow>
								)
							})}
						</TableBody>
					)}
				</Table>
				{totalCnt === 0 && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '90%',
							color: '#AAAAAA',
							fontSize: '25px',
						}}
					>
						&nbsp;There are no search results.
						<br /> Please try another keywords.
					</Box>
				)}
			</TableContainer>
			<Grid container spacing={1}>
				<Grid item md={11}>
					<TablePagination
						component="div"
						count={totalCnt}
						rowsPerPage={rowsPerPage}
						page={page - 1}
						SelectProps={{
							inputProps: {
								'aria-label': 'rows per page',
							},
							native: true,
						}}
						onPageChange={onPageChange}
						onRowsPerPageChange={onRowsPerPageChange}
					/>
				</Grid>
			</Grid>
		</>
	)
}
